import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { QUIZ } from "@/lib/quiz";
import { Camera, Upload, RefreshCw, ArrowRight, ArrowLeft, Lock, Check } from "lucide-react";

const HERO_BLUR = "https://images.unsplash.com/photo-1551184451-76b762941ad6?auto=format&fit=crop&w=500&q=60";

export default function Create() {
  const [step, setStep] = useState(0); // 0 selfie, 1 quiz, 2 pay
  const [selfie, setSelfie] = useState(null);
  const [answers, setAnswers] = useState({});
  const [camOn, setCamOn] = useState(false);
  const [paying, setPaying] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => () => stopCam(), []);

  const startCam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      setCamOn(true);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 50);
    } catch {
      toast.error("Couldn't access camera. You can upload a photo instead.");
    }
  };

  const stopCam = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamOn(false);
  };

  const capture = () => {
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    setSelfie(canvas.toDataURL("image/png"));
    stopCam();
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setSelfie(reader.result);
    reader.readAsDataURL(file);
  };

  const allAnswered = QUIZ.every((q) => answers[q.key]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data: gen } = await api.post("/generations", {
        selfie_base64: selfie,
        quiz: answers,
      });
      const { data: checkout } = await api.post("/payments/checkout", {
        generation_id: gen.generation_id,
        origin_url: window.location.origin,
      });
      // Stripe Checkout can't render inside an iframe (X-Frame-Options: DENY).
      // When embedded (e.g. the preview pane) open checkout in a new top-level tab.
      const inIframe = window.self !== window.top;
      if (inIframe) {
        const win = window.open(checkout.url, "_blank");
        if (!win) {
          try {
            window.top.location.href = checkout.url;
          } catch (_) {
            window.location.href = checkout.url;
          }
        } else {
          toast.success("Opening secure checkout in a new tab…");
        }
        setPaying(false);
      } else {
        window.location.href = checkout.url;
      }
    } catch (err) {
      toast.error(apiError(err));
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-24">
        <Stepper step={step} />

        <AnimatePresence mode="wait">
          {step === 0 && (
            <Section key="selfie">
              <h2 className="font-heading text-2xl sm:text-3xl text-charcoal mb-2">Take your selfie</h2>
              <p className="text-sm text-muted-c mb-8">Face the camera, good lighting, no sunglasses.</p>

              <div className="grid md:grid-cols-2 gap-8 items-start">
                <div className="relative rounded-3xl overflow-hidden bg-black/5 border border-black/5 aspect-[4/5] flex items-center justify-center">
                  {selfie ? (
                    <img src={selfie} alt="selfie" data-testid="selfie-preview" className="w-full h-full object-cover" />
                  ) : camOn ? (
                    <>
                      <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                      <div className="absolute inset-6 rounded-3xl border-2 border-white/60 pointer-events-none" />
                    </>
                  ) : (
                    <div className="text-center text-muted-c px-6">
                      <Camera className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">Start the camera or upload a photo</p>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  {!selfie && !camOn && (
                    <>
                      <button data-testid="start-camera-btn" onClick={startCam} className="btn-primary w-full py-3.5 font-semibold flex items-center justify-center gap-2">
                        <Camera className="w-5 h-5" /> Start Camera
                      </button>
                      <button data-testid="upload-btn" onClick={() => fileRef.current.click()} className="w-full py-3.5 font-semibold rounded-full border border-black/10 hover:bg-black/5 transition-colors flex items-center justify-center gap-2">
                        <Upload className="w-5 h-5" /> Upload a Photo
                      </button>
                      <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" />
                    </>
                  )}
                  {camOn && (
                    <button data-testid="capture-btn" onClick={capture} className="btn-primary w-full py-3.5 font-semibold flex items-center justify-center gap-2">
                      <Camera className="w-5 h-5" /> Capture
                    </button>
                  )}
                  {selfie && (
                    <>
                      <button data-testid="retake-btn" onClick={() => { setSelfie(null); }} className="w-full py-3.5 font-semibold rounded-full border border-black/10 hover:bg-black/5 transition-colors flex items-center justify-center gap-2">
                        <RefreshCw className="w-5 h-5" /> Retake
                      </button>
                      <button data-testid="selfie-next-btn" onClick={() => setStep(1)} className="btn-primary w-full py-3.5 font-semibold flex items-center justify-center gap-2">
                        Continue to Habits <ArrowRight className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </Section>
          )}

          {step === 1 && (
            <Section key="quiz">
              <h2 className="font-heading text-2xl sm:text-3xl text-charcoal mb-2">Your daily habits</h2>
              <p className="text-sm text-muted-c mb-8">Be honest — this shapes your future portrait.</p>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                {QUIZ.map((q) => (
                  <div key={q.key} className={`bg-white rounded-3xl p-6 border border-black/5 soft-shadow ${q.span}`}>
                    <div className="text-xs tracking-[0.15em] uppercase font-bold text-sage mb-1">{q.label}</div>
                    <div className="font-heading text-lg text-charcoal mb-4">{q.question}</div>
                    <div className="grid grid-cols-3 gap-2">
                      {q.options.map((o) => {
                        const active = answers[q.key] === o.value;
                        return (
                          <button
                            key={o.value}
                            data-testid={`quiz-${q.key}-${o.value}`}
                            onClick={() => setAnswers({ ...answers, [q.key]: o.value })}
                            className="quiz-card rounded-2xl p-3 text-left border-2"
                            style={{
                              borderColor: active ? "var(--primary)" : "rgba(26,26,26,0.08)",
                              background: active ? "rgba(232,141,103,0.08)" : "#FAF8F5",
                            }}
                          >
                            <div className="font-semibold text-sm text-charcoal">{o.title}</div>
                            <div className="text-[11px] text-muted-c mt-0.5">{o.desc}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mt-8">
                <button data-testid="quiz-back-btn" onClick={() => setStep(0)} className="flex items-center gap-2 text-sm font-semibold text-muted-c hover:text-charcoal transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button data-testid="quiz-next-btn" disabled={!allAnswered} onClick={() => setStep(2)} className="btn-primary px-8 py-3.5 font-semibold flex items-center gap-2">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </Section>
          )}

          {step === 2 && (
            <Section key="pay">
              <div className="grid md:grid-cols-2 gap-10 items-center">
                <div className="relative rounded-3xl overflow-hidden aspect-[4/5] border border-black/5">
                  <img src={HERO_BLUR} alt="future self" className="w-full h-full object-cover blur-xl scale-110" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Lock className="w-10 h-10 text-white/90" />
                  </div>
                </div>
                <div>
                  <div className="text-xs tracking-[0.15em] uppercase font-bold text-sage mb-3">Final step</div>
                  <h2 className="font-heading text-3xl text-charcoal mb-4">Unlock your future self</h2>
                  <p className="text-sm text-muted-c leading-relaxed mb-6">
                    Our AI is ready to age your selfie 20 years based on your habits, calculate your
                    health score, and hand you personalised glow-up tips.
                  </p>
                  <div className="bg-white rounded-3xl p-6 border border-black/5 soft-shadow mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="font-heading text-4xl text-charcoal">$7</span>
                      <span className="text-sm text-muted-c">/ reveal</span>
                    </div>
                    <ul className="mt-4 space-y-2">
                      {["AI-aged portrait (20 years)", "Personal health score", "Tailored glow-up tips", "Saved to your history"].map((f) => (
                        <li key={f} className="flex items-center gap-2 text-sm text-charcoal">
                          <Check className="w-4 h-4 text-sage" style={{ color: "var(--sage)" }} /> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button data-testid="pay-btn" onClick={pay} disabled={paying} className="btn-primary w-full py-4 font-semibold text-base">
                    {paying ? "Redirecting to checkout…" : "Pay $7 & Reveal"}
                  </button>
                  <div data-testid="stripe-trust" className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-c">
                    <Lock className="w-3.5 h-3.5" style={{ color: "var(--sage)" }} />
                    Payments secured by Stripe · encrypted &amp; PCI-compliant
                  </div>
                  <button data-testid="pay-back-btn" onClick={() => setStep(1)} className="mt-4 w-full text-sm text-muted-c hover:text-charcoal transition-colors">
                    Back to habits
                  </button>
                </div>
              </div>
            </Section>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

const Section = ({ children }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }}>
    {children}
  </motion.div>
);

const Stepper = ({ step }) => {
  const labels = ["Selfie", "Habits", "Reveal"];
  return (
    <div className="flex items-center gap-3 mb-10">
      {labels.map((l, i) => (
        <div key={l} className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors"
              style={{
                background: i <= step ? "var(--primary)" : "rgba(26,26,26,0.06)",
                color: i <= step ? "#fff" : "var(--muted)",
              }}
            >
              {i + 1}
            </div>
            <span className="text-sm font-semibold" style={{ color: i <= step ? "var(--charcoal)" : "var(--muted)" }}>{l}</span>
          </div>
          {i < 2 && <div className="w-8 h-px bg-black/10" />}
        </div>
      ))}
    </div>
  );
};
